import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { processLine } from './utils/pgnUtils';
import { getCongratulationsMessage } from './utils/congratulations';
import chessAudio from './utils/chessAudio';
import { trackLineSelection, trackGameCompletion, trackReset, trackUIInteraction, trackNavigation } from './utils/gtm';
import { getCards, getLineStatus, getProgress, markLearned, recordReview, formatDue, subscribe } from './utils/srs';

// Hooks
import { useGameState } from './hooks/useGameState';
import { useUIState } from './hooks/useUIState';

// Services
import { MoveHandler } from './services/MoveHandler';
import { ComputerPlayer } from './services/ComputerPlayer';

// Components
import GameHeader from './components/ui/GameHeader';
import ChessBoard, { useChessInteraction } from './components/chess/ChessBoard';
import GameControls from './components/chess/GameControls';
import GameSidebar from './components/ui/GameSidebar';
import CelebrationOverlay from './components/ui/CelebrationOverlay';
import AddLineModal from './components/AddLineModal';

/**
 * Opening Trainer - Main Component
 * Single Responsibility: Orchestrating chess training session
 * Dependency Inversion: Depends on service abstractions
 * Open/Closed: Extensible with new features without core changes
 */
function OpeningTrainer({ opening, currentLineId, onBack, onLineChange, refreshOpenings }) {
  // ==================== STATE HOOKS ====================
  const gameState = useGameState();
  const uiState = useUIState();
  const [mode, setMode] = useState('learn'); // 'learn' | 'practice'
  const [cards, setCards] = useState(getCards);
  const completedSessionRef = useRef(null);

  // Keep spaced repetition progress in sync
  useEffect(() => subscribe(() => setCards(getCards())), []);

  const progress = useMemo(() => getProgress(opening?.lines, cards), [opening, cards]);

  // Next line to work on: unlearned lines in Learn mode, due lines in Practice mode
  const findNextLine = useCallback((targetMode, excludeLineId = null) => {
    const lines = (opening?.lines || []).filter(line => line.id !== excludeLineId);
    const wanted = targetMode === 'learn' ? 'new' : 'due';
    return lines.find(line => getLineStatus(line.id, cards) === wanted) || null;
  }, [opening, cards]);

  // ==================== SERVICES ====================
  const moveHandlerRef = useRef(null);
  const computerPlayerRef = useRef(null);
  const isNavigatingBackRef = useRef(false);
  
  // Enhance gameState with opening data
  const enhancedGameState = { ...gameState, opening, mode };
  
  if (!moveHandlerRef.current) {
    moveHandlerRef.current = new MoveHandler(enhancedGameState, uiState);
  } else {
    // Update the gameState and uiState references
    moveHandlerRef.current.gameState = enhancedGameState;
    moveHandlerRef.current.uiState = uiState;
  }
  
  if (!computerPlayerRef.current) {
    computerPlayerRef.current = new ComputerPlayer(enhancedGameState, uiState);
  } else {
    // Update the gameState and uiState references
    computerPlayerRef.current.gameState = enhancedGameState;
    computerPlayerRef.current.uiState = uiState;
  }
  
  const moveHandler = moveHandlerRef.current;
  const computerPlayer = computerPlayerRef.current;

  // ==================== CHESS INTERACTIONS ====================
  const { onSquareClick, onPieceDrop } = useChessInteraction(gameState, uiState, moveHandler, opening);

  // ==================== NAVIGATION HANDLERS ====================
  const goToPreviousMove = useCallback(() => {
    if (!gameState.currentLine || gameState.moveIndex <= 0) return;
    
    // Track previous move navigation
    trackNavigation(
      'previous_move',
      `${opening?.name} - ${gameState.currentLine?.name}`,
      `Move ${gameState.moveIndex}`
    );
    
    const newMoveIndex = gameState.moveIndex - 1;
    const reconstructedGame = gameState.reconstructGameToMove(newMoveIndex);
    
    if (reconstructedGame) {
      isNavigatingBackRef.current = true;
      moveHandler.cancelRevert();
      gameState.setGame(reconstructedGame);
      gameState.setMoveIndex(newMoveIndex);
      uiState.setFeedback(`Moved back to move ${newMoveIndex + 1}`);
      uiState.clearGameState();
      const previousMove = reconstructedGame.history({ verbose: true }).pop();
      uiState.setLastMove(previousMove ? { from: previousMove.from, to: previousMove.to } : null);
      // Reset the flag after a short delay
      setTimeout(() => {
        isNavigatingBackRef.current = false;
      }, 100);
    }
  }, [gameState, uiState, moveHandler, opening?.name]);

  const goToNextMove = useCallback(() => {
    // Ignore while the computer is about to reply, otherwise its move would be played twice
    if (!gameState.isPlayerTurn()) return;
    
    // Track next move navigation  
    trackNavigation(
      'next_move',
      `${opening?.name} - ${gameState.currentLine?.name}`,
      `Move ${gameState.moveIndex + 1}`
    );
    
    const result = moveHandler.playHint();
    if (result && !result.error) {
      gameState.incrementHints();
    }
    if (result) {
      if (result.error) {
        uiState.setFeedback(result.error);
      } else {
        uiState.setFeedback(result.message);
        uiState.clearGameState();
        // playHint returns the game directly and updates moveIndex internally
        computerPlayer.autoPlayMoves(result.game, gameState.moveIndex);
      }
    }
  }, [moveHandler, uiState, computerPlayer, gameState, opening?.name]);

  const playHint = useCallback(() => {
    trackUIInteraction('hint_button', 'click', `${opening?.name} - ${gameState.currentLine?.name}`);
    goToNextMove();
  }, [goToNextMove, opening?.name, gameState.currentLine?.name]);

  const resetOpening = useCallback(() => {
    // Track reset action
    trackReset(
      opening?.name || 'unknown',
      gameState.currentLine?.name || 'unknown',
      gameState.moveIndex
    );
    
    computerPlayer.cancel();
    moveHandler.cancelRevert();
    const newGame = gameState.resetGame();
    uiState.resetUI();
    // chessAudio.playGameStartSound();
    computerPlayer.autoPlayMoves(newGame, 0);
  }, [gameState, uiState, computerPlayer, moveHandler, opening?.name]);

  const goToNextLine = useCallback(() => {
    if (!opening?.lines || !gameState.currentLine) return;
    
    const currentIndex = opening.lines.findIndex(line => line.id === gameState.currentLine.id);
    const nextLine = findNextLine(mode, gameState.currentLine.id) || opening.lines[currentIndex + 1];
    if (!nextLine) return;
    
    // Track line change
    trackLineSelection(
      opening.name,
      nextLine.name,
      nextLine.moves?.length || 0
    );
    
    // The line initialization effect resets the board once the URL changes
    onLineChange(nextLine.id);
  }, [opening, gameState.currentLine, findNextLine, mode, onLineChange]);

  // ==================== MODES ====================
  const changeMode = useCallback((newMode) => {
    trackUIInteraction('mode_switch', 'click', newMode);
    setMode(newMode);
    const target = findNextLine(newMode);
    
    if (!target) {
      uiState.setFeedback(newMode === 'practice'
        ? (progress.learned === 0
          ? 'Learn a line first, then come back to practice it.'
          : `Nothing to review right now. Next review ${formatDue(progress.nextDue)}. You can still pick any line to practice.`)
        : 'You have discovered every line of this course! 🎉');
      return;
    }
    
    if (target.id === gameState.currentLine?.id) {
      resetOpening();
    } else {
      onLineChange(target.id);
    }
  }, [findNextLine, uiState, progress, gameState.currentLine, onLineChange, resetOpening]);

  // ==================== LINE MANAGEMENT ====================
  const handleLineChange = useCallback((e) => {
    const selectedLineId = e.target.value;
    const lineIdToMatch = isNaN(selectedLineId) ? selectedLineId : Number(selectedLineId);
    const rawLine = opening?.lines?.find(line => line.id === lineIdToMatch);
    
    if (rawLine && onLineChange) {
      // Track line selection
      trackLineSelection(
        opening.name,
        rawLine.name,
        rawLine.moves?.length || 0
      );
      
      // The line initialization effect resets the board once the URL changes
      onLineChange(lineIdToMatch);
    }
  }, [opening, onLineChange]);

  // ==================== LINE COMPLETION HANDLER ====================
  const handleLineCompletion = useCallback(() => {
    if (!gameState.isLineComplete()) return;
    
    // Only handle each completed session once
    const sessionKey = `${gameState.currentLine.id}-${gameState.startTime}`;
    if (completedSessionRef.current === sessionKey) return;
    completedSessionRef.current = sessionKey;
    
    // Spaced repetition
    const lineId = gameState.currentLine.id;
    if (mode === 'learn') {
      markLearned(lineId);
      uiState.setFeedback('Line learned! It is now in your practice queue.');
    } else {
      const success = gameState.mistakes === 0 && gameState.hintsUsed === 0;
      const card = recordReview(lineId, success);
      uiState.setFeedback(success
        ? `Perfect! Next review ${formatDue(card.due)}.`
        : `Line completed with ${gameState.mistakes} mistake(s) and ${gameState.hintsUsed} hint(s). It will come back ${formatDue(card.due)}.`);
    }
    
    chessAudio.playGameEndSound();
    const completionTime = gameState.startTime ? (Date.now() - gameState.startTime) / 1000 : 0;
    const totalMoves = gameState.currentLine?.moves.length || 0;
    const correctMoves = totalMoves - gameState.mistakes;
    
    // Track game completion
    trackGameCompletion(
      opening?.name || 'unknown',
      gameState.currentLine?.name || 'unknown',
      totalMoves,
      correctMoves,
      completionTime
    );
    
    // Delay showing congratulations by 1.5 seconds
    setTimeout(() => {
      const congratulations = getCongratulationsMessage({
        openingName: opening?.name || '',
        lineName: gameState.currentLine?.name || '',
        moveCount: totalMoves,
        mistakes: gameState.mistakes,
        completionTime,
        firstAttempt: gameState.isFirstAttempt,
        streak: 0
      });
      
      uiState.setCongratulationsData(congratulations);
      uiState.setShowCelebration(true);
      
      // Auto-hide celebration after 5 seconds
      setTimeout(() => {
        uiState.setShowCelebration(false);
      }, 5000);
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.isLineComplete, gameState.startTime, gameState.currentLine, gameState.mistakes, gameState.hintsUsed, gameState.isFirstAttempt, opening?.name, mode]);

  // ==================== EFFECTS ====================
  
  // Audio settings effect
  useEffect(() => {
    chessAudio.setEnabled(uiState.soundEnabled);
  }, [uiState.soundEnabled]);

  // Line initialization effect
  useEffect(() => {
    if (opening?.lines?.length > 0) {
      let lineToSet;
      
      // Find specific line if ID provided
      if (currentLineId) {
        const lineIdToMatch = isNaN(currentLineId) ? currentLineId : Number(currentLineId);
        lineToSet = opening.lines.find(line => line.id === lineIdToMatch);
      }
      
      // No line in the URL: continue with the next line to learn (or the first one)
      if (!lineToSet) {
        const defaultLine = findNextLine('learn') || opening.lines[0];
        onLineChange(defaultLine.id);
        return;
      }
      
      if (lineToSet) {
        const processedLine = processLine(lineToSet, opening.playerColor);
        computerPlayer.cancel();
        moveHandler.cancelRevert();
        gameState.setCurrentLine(processedLine);
        const newGame = gameState.resetGame();
        uiState.resetUI();
        // chessAudio.playGameStartSound();
        
        // Use timeout to avoid infinite loop
        setTimeout(() => {
          computerPlayer.autoPlayMoves(newGame, 0);
        }, 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening, currentLineId, onLineChange]);

  // Line completion check effect
  useEffect(() => {
    handleLineCompletion();
  }, [gameState.moveIndex, handleLineCompletion]);

  // Auto-play computer moves after player moves
  useEffect(() => {
    if (gameState.currentLine && gameState.moveIndex < gameState.currentLine.moves.length) {
      // Check if it's computer's turn and auto-play (but not when navigating backwards)
      if (!gameState.isPlayerTurn() && !isNavigatingBackRef.current) {
        computerPlayer.autoPlayMoves(gameState.game, gameState.moveIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.moveIndex, gameState.currentLine, gameState.isPlayerTurn]);

  // ==================== EARLY RETURN ====================
  if (!opening || !opening.lines || opening.lines.length === 0) {
    return (
      <div className="h-[100dvh] flex items-center justify-center p-4">
        <div className="card p-8 text-center max-w-sm">
          <div className="text-lg font-bold text-ink-100 mb-1">This course has no lines yet</div>
          <p className="text-sm text-ink-400 mb-5">Pick another course from the catalog.</p>
          <button 
            onClick={() => {
              trackNavigation('back_to_courses', opening?.name || 'unknown_opening', 'homepage');
              onBack();
            }}
            className="btn-primary"
          >
            Back to courses
          </button>
        </div>
      </div>
    );
  }

  const learnedPercent = progress.total ? (progress.learned / progress.total) * 100 : 0;

  // ==================== RENDER ====================
  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden">
      {/* Header */}
      <GameHeader
        openingName={opening?.name}
        playerColor={opening?.playerColor}
        soundEnabled={uiState.soundEnabled}
        onBack={onBack}
        onSoundToggle={() => uiState.setSoundEnabled(!uiState.soundEnabled)}
      />

      {/* Course progress */}
      <div className="h-1 bg-ink-800" title={`${progress.learned}/${progress.total} lines discovered`}>
        <div className="h-full bg-brand-400 transition-all duration-500" style={{ width: `${learnedPercent}%` }} />
      </div>

      {/* Main content */}
      <main className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
        <div className="max-w-7xl mx-auto w-full h-full p-3 sm:p-4 lg:p-6 flex flex-col lg:flex-row items-center lg:items-start justify-start lg:justify-center gap-4 lg:gap-6">
          {/* Chess board section */}
          <div className="flex flex-col items-center gap-3 w-full lg:w-auto">
            <div className="relative">
              <ChessBoard
                game={gameState.game}
                selectedSquare={uiState.selectedSquare}
                possibleMoves={uiState.possibleMoves}
                lastMove={uiState.lastMove}
                moveBadge={uiState.moveBadge}
                playerColor={opening?.playerColor}
                showCelebration={uiState.showCelebration}
                onSquareClick={onSquareClick}
                onPieceDrop={onPieceDrop}
              />
              
              <CelebrationOverlay
                show={uiState.showCelebration}
                congratulationsData={uiState.congratulationsData}
                currentLine={gameState.currentLine}
                openingName={opening?.name}
                mistakes={gameState.mistakes}
              />
            </div>
            
            {/* Control Buttons */}
            <GameControls
              currentLine={gameState.currentLine}
              moveIndex={gameState.moveIndex}
              onPrevious={goToPreviousMove}
              onNext={goToNextMove}
              onHint={playHint}
              onReset={resetOpening}
              onNextLine={goToNextLine}
              hasNextLine={!!(opening?.lines && gameState.currentLine && (
                findNextLine(mode, gameState.currentLine.id) ||
                opening.lines.findIndex(line => line.id === gameState.currentLine.id) < opening.lines.length - 1
              ))}
            />
          </div>

          {/* Sidebar */}
          <GameSidebar
            opening={opening}
            currentLine={gameState.currentLine}
            currentLineId={currentLineId}
            moveIndex={gameState.moveIndex}
            isPlayerTurn={gameState.isPlayerTurn()}
            game={gameState.game}
            selectedSquare={uiState.selectedSquare}
            possibleMoves={uiState.possibleMoves}
            feedback={uiState.feedback}
            onLineChange={handleLineChange}
            mode={mode}
            onModeChange={changeMode}
            progress={progress}
            cards={cards}
          />
          
          {/* Add Line Modal */}
          {uiState.showAddLineModal && (
            <AddLineModal
              currentOpeningId={opening?.id}
              onClose={() => uiState.setShowAddLineModal(false)}
              onSuccess={() => {
                uiState.setShowAddLineModal(false);
                refreshOpenings();
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default OpeningTrainer;
